
import React, { useState, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UserProfile } from "@/contexts/auth/types";
import { CheckCircle } from "lucide-react";
import AvatarUpload from "./AvatarUpload";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/auth";
import CountryFlag from "@/components/country/CountryFlag";
import { Badge } from "@/components/ui/badge";

interface ProfileHeaderProps {
  user: UserProfile;
  getInitials: () => string;
  setActiveTab?: (tab: string) => void;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({ user, getInitials, setActiveTab }) => {
  const { user: authUser } = useAuth();
  const [verifiedCountries, setVerifiedCountries] = useState<string[]>([]);

  // Check if the user is a verified pollster
  const isVerifiedPollster = user.profile?.role === "pollster" && user.profile?.verified === true;

  useEffect(() => {
    const fetchVerifiedCountries = async () => {
      if (!authUser?.id) return;

      try {
        const { data, error } = await supabase
          .from('user_country_verifications')
          .select('country_code')
          .eq('user_id', authUser.id)
          .eq('verification_status', 'verified');

        if (error) throw error;

        const countries = data?.map(item => item.country_code) || [];
        setVerifiedCountries(countries);
      } catch (error) {
        console.error('Error fetching verified countries:', error);
      }
    };

    fetchVerifiedCountries();
  }, [authUser?.id]);

  const handleVerifiedCountriesClick = () => {
    if (setActiveTab) {
      setActiveTab('verification');
    }
  };
  
  return (
    <div className="flex flex-col items-center mb-8">
      <AvatarUpload />
      
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2 mt-4">
        {user.profile?.name || "User"}
        {isVerifiedPollster && (
          <CheckCircle className="h-5 w-5 text-blue-500" aria-label="Verified Pollster" />
        )}
      </h1>
      <p className="text-gray-500 dark:text-gray-400">{user.email}</p>
      <Badge variant="secondary" className="mt-2 text-sm font-medium">
        {user.profile?.role.replace("_", " ").toUpperCase()}
      </Badge>
      
      {verifiedCountries.length > 0 && (
        <div 
          className="mt-3 flex items-center gap-2 text-sm text-green-600 cursor-pointer hover:text-green-700 transition-colors"
          onClick={handleVerifiedCountriesClick}
        >
          <span>Verified in:</span>
          {verifiedCountries.map((countryCode, index) => (
            <div key={countryCode} className="flex items-center gap-1">
              <CountryFlag countryCode={countryCode} className="h-4 w-5" />
              <span>{countryCode}</span>
              {index < verifiedCountries.length - 1 && <span>,</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProfileHeader;
